import {useContext, useState} from 'react';
import {Controller, useForm} from "react-hook-form";
import {Box, Button, InputAdornment, makeStyles, Typography} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import {putFieldOptions} from "api";
import {SettingsContext} from 'components/Routes/application/utils';
import Alert from '@material-ui/lab/Alert';
import SaveIcon from '@material-ui/icons/Save';
import BlockIcon from '@material-ui/icons/Block';
import messages from '@super-template/messages';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import FormInput from './FormInput';
import {mapValues} from 'lodash';

const useStyles = makeStyles((theme) => ({
  editingArea: {
    padding: theme.spacing(4),
    border: `2px solid ${theme.palette.primary.main}`,
  },
  descriptionArea: {
    display: 'flex',
    alignItems: 'center'
  },
  quill: {
    ...theme.shape,
    width: '100%',
    border: `1px solid rgba(0, 0, 0, 0.26)`,
    '& .ql-container': {
      font: 'inherit',

    },
    '& .ql-editor': {
      minHeight: 85,
      fontSize: '1rem',
    },
    '& .ql-tooltip': {
      zIndex: 2,
      '&.ql-editing': {
        left: '0 !important',
        top: '25px !important',
        minWidth: '100%',
        "& .ql-tooltip-arrow": {
          display: 'none !important'
        }
      }
    },
  }
}));

const Adornment = ({l}) => (
  <InputAdornment position="start">
    <img height={20} style={{border: '1px solid gray'}} src={`${process.env.PUBLIC_URL}/images/lang_${l}.svg`} alt={l}/>
  </InputAdornment>
);

export default ({fieldOptions, id, close, info}) => {
  const intl = useIntl();
  const classes = useStyles();
  const {language} = useContext(SettingsContext);
  const [error, setError] = useState(false);
  const {control, handleSubmit} = useForm({defaultValues: fieldOptions});
  let languages = language?.languages.map((l) => l.replace('-', '_'));
  if (languages.length < 1) languages = ['fi_FI'];
  const formats = info ? ['link', 'bold'] : ['link'];

  const hasSaved = ({title, description, text, error}) => {
    if (error) {
      setError(true);
      document.getElementById(id).scrollIntoView({behavior: 'smooth'});
    } else {
      if (info) {
        fieldOptions.text = text;
      } else {
        fieldOptions.description = description;
        fieldOptions.title = title;
      }
      close();
    }
  };

  const formValid = ({title, description, text, fieldKey, key}) => {
    if (info) {
      putFieldOptions({key, text, isFormField: false}).then(hasSaved);
    } else {
      const trimmed = mapValues(description, v => v.replaceAll('<p><br></p>', ''));
      putFieldOptions({fieldKey, title, description: trimmed, isFormField: true}).then(hasSaved);
    }
  };

  return (
    <div className={info ? '' : classes.editingArea} id={id}>
      {error &&
      <Alert severity="error" style={{marginBottom: 30}}>
        <Typography><FormattedMessage {...messages.save_error} /></Typography>
      </Alert>}
      {info ?
        <h4 style={{marginTop: 0}}><FormattedMessage {...messages.edit_info_header} /></h4> :
        <>
          <h4><FormattedMessage {...messages.edit_header} /></h4>
          <span><FormattedMessage {...messages.edit_hint} /></span>
        </>}
      {languages.length > 1 &&
      <span>{' '}<FormattedMessage {...messages.lang_version} values={{count: languages.length}}/></span>}
      {!info && <h4><FormattedMessage {...messages.header} /></h4>}
      {!info && languages.map((l) => (
        <Controller
          control={control} name={`title.${l}`} defaultValue={''} rules={{required: true}} key={l + "title"}
          render={({fieldState: {error}, field: {ref, ...field}}) =>
            <FormInput error={!!error} key={`${l}_l`} required fullWidth {...field} inputRef={ref}
                       placeholder={intl.formatMessage(messages.header)} startAdornment={<Adornment l={l}/>}/>}/>))}
      <h4><FormattedMessage {...messages.description} /></h4>
      {languages.map(l => (
        <div className={classes.descriptionArea} key={l + "description"}>
          <Adornment l={l}/>
          <Controller
            control={control} name={`${info ? 'text' : 'description'}.${l}`} defaultValue={''}
            render={({fieldState: {error}, field: {onChange, value}}) =>
              <ReactQuill
                className={classes.quill} onChange={onChange} value={value} theme={'bubble'} error={!!error}
                modules={{toolbar: [formats]}} formats={formats} style={{background: 'white'}}/>}/>
        </div>))}
      <Box display={'flex'} justifyContent="space-between" flexWrap="wrap" style={{marginTop: 25}}>
        <Button variant="contained" color="primary" size="small" startIcon={<SaveIcon/>}
                onClick={handleSubmit(formValid)}>
          <FormattedMessage {...messages.save} />
        </Button>
        <Button variant="outlined" color="primary" size="small" startIcon={<BlockIcon/>} onClick={close}>
          <FormattedMessage {...messages.cancel} />
        </Button>
      </Box>
    </div>
  );
};
